import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Grid2x2, X } from "lucide-react";

// All gallery photos served from /public/gallery/
// Grouped by location: exterior first, then bedrooms (each themed room + its bath),
// then living, entry, dining, kitchen, decor, garage/games, utility, and exterior/yard.
// IMG_6658 (backyard) intentionally placed last per request.
const PHOTOS = [
  // Exterior (front)
  "IMG_6654",
  // Shrek bedroom + ensuite bath
  "IMG_6670", "IMG_6671", "IMG_6683", "IMG_6672", "IMG_6673",
  // SpongeBob bedroom + bath
  "IMG_6715", "IMG_6717", "IMG_6684", "IMG_6718", "IMG_6719",
  // Trolls bedroom
  "IMG_6681",
  // Minions bunk room
  "IMG_6712", "IMG_6714", "IMG_6682",
  // Living / family room
  "IMG_6664", "IMG_6665", "IMG_6666", "IMG_6663", "IMG_6667",
  "IMG_6705", "IMG_6708", "IMG_6709", "IMG_6679", "IMG_6707", "IMG_6710",
  // Entry / hallway
  "IMG_6668", "IMG_6711",
  // Dining
  "IMG_6703", "IMG_6678",
  // Kitchen
  "IMG_6660", "IMG_6661", "IMG_6662", "IMG_6680",
  // Decor detail
  "IMG_6677",
  // Garage / games
  "IMG_6675",
  // Utility (laundry, closet)
  "IMG_6674", "IMG_6676",
  // Exterior / yard
  "IMG_6691", "IMG_6687", "IMG_6653",
  "IMG_6658", // backyard — last
];

const SLIDES = PHOTOS.map((id) => ({
  key: id,
  image: `${process.env.PUBLIC_URL || ""}/gallery/${id}.JPG`,
}));

export default function Gallery() {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [gridOpen, setGridOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null); // null | number

  // ESC + arrow keys for modal/lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIdx !== null) {
        if (e.key === "Escape") setLightboxIdx(null);
        if (e.key === "ArrowRight") setLightboxIdx((i) => Math.min(SLIDES.length - 1, (i ?? 0) + 1));
        if (e.key === "ArrowLeft") setLightboxIdx((i) => Math.max(0, (i ?? 0) - 1));
      } else if (gridOpen && e.key === "Escape") {
        setGridOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridOpen, lightboxIdx]);

  // Lock body scroll while modal open
  useEffect(() => {
    const open = gridOpen || lightboxIdx !== null;
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [gridOpen, lightboxIdx]);

  const updateButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const slideWidth = el.firstChild?.offsetWidth || el.clientWidth;
    const gapPx = parseFloat(getComputedStyle(el).columnGap || "0") || 0;
    const step = slideWidth + gapPx;
    const idx = Math.round(el.scrollLeft / Math.max(1, step));
    setActiveIdx(Math.min(SLIDES.length - 1, Math.max(0, idx)));
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateButtons();
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, []);

  const scrollToIndex = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.min(SLIDES.length - 1, Math.max(0, i));
    const target = el.children[clamped];
    if (target) {
      el.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
    }
  };

  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slideWidth = el.firstChild?.offsetWidth || el.clientWidth;
    const gapPx = parseFloat(getComputedStyle(el).columnGap || "0") || 0;
    const step = slideWidth + gapPx;
    const currentIdx = Math.round(el.scrollLeft / Math.max(1, step));
    scrollToIndex(currentIdx + dir);
  };

  const goTo = (i) => scrollToIndex(i);

  const showDots = SLIDES.length <= 12;

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 sm:py-24 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block bg-brand-red text-white font-fredoka font-bold rounded-full px-4 py-1.5 border-4 border-slate-900 neo-shadow-sm rotate-[2deg]">
            PHOTO GALLERY
          </span>
          <h2 className="font-fredoka font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 text-slate-900">
            Just <span className="text-brand-blue">peek inside.</span>
          </h2>
        </div>

        <div className="relative">
          {/* Prev */}
          <button
            data-testid="gallery-prev"
            onClick={() => scrollByDir(-1)}
            disabled={!canPrev}
            aria-label="Previous photo"
            className="hidden md:grid absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-14 h-14 place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm hover:bg-brand-yellow disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={26} strokeWidth={3} />
          </button>
          {/* Next */}
          <button
            data-testid="gallery-next"
            onClick={() => scrollByDir(1)}
            disabled={!canNext}
            aria-label="Next photo"
            className="hidden md:grid absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-14 h-14 place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm hover:bg-brand-yellow disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={26} strokeWidth={3} />
          </button>

          {/* Scroller */}
          <div
            ref={scrollerRef}
            data-testid="gallery-scroller"
            className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar gap-4 sm:gap-6 rounded-3xl"
            style={{ scrollbarWidth: "none" }}
          >
            {SLIDES.map((s, i) => (
              <div
                key={s.key}
                data-testid={`gallery-slide-${s.key}`}
                className="snap-start shrink-0 w-full"
              >
                <div className="relative bg-white border-4 border-slate-900 rounded-3xl overflow-hidden neo-shadow">
                  <img
                    src={s.image}
                    alt={`Gallery photo ${i + 1}`}
                    loading={i < 2 ? "eager" : "lazy"}
                    className="w-full h-[360px] sm:h-[480px] lg:h-[560px] object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-brand-yellow border-4 border-slate-900 rounded-full px-3 py-1 neo-shadow-sm font-fredoka font-bold text-xs sm:text-sm text-slate-900">
                    {i + 1} / {SLIDES.length}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile arrows + dots / counter */}
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            data-testid="gallery-prev-mobile"
            onClick={() => scrollByDir(-1)}
            disabled={!canPrev}
            aria-label="Previous photo"
            className="md:hidden w-12 h-12 grid place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm disabled:opacity-40"
          >
            <ChevronLeft size={22} strokeWidth={3} />
          </button>
          {showDots ? (
            <div className="flex items-center gap-2">
              {SLIDES.map((s, i) => (
                <button
                  key={s.key}
                  data-testid={`gallery-dot-${i}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to photo ${i + 1}`}
                  className={`h-3 rounded-full border-2 border-slate-900 transition-all ${
                    i === activeIdx ? "w-8 bg-brand-red" : "w-3 bg-white"
                  }`}
                />
              ))}
            </div>
          ) : (
            <div className="font-fredoka font-bold text-base text-slate-900 bg-white border-4 border-slate-900 rounded-full px-5 py-2 neo-shadow-sm tabular-nums">
              {activeIdx + 1} / {SLIDES.length}
            </div>
          )}
          <button
            data-testid="gallery-next-mobile"
            onClick={() => scrollByDir(1)}
            disabled={!canNext}
            aria-label="Next photo"
            className="md:hidden w-12 h-12 grid place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm disabled:opacity-40"
          >
            <ChevronRight size={22} strokeWidth={3} />
          </button>
        </div>

        {/* View Full Gallery CTA */}
        <div className="mt-8 flex justify-center">
          <button
            data-testid="gallery-view-all"
            onClick={() => setGridOpen(true)}
            className="font-fredoka font-bold text-lg bg-brand-blue text-white border-4 border-slate-900 rounded-full px-7 py-3 neo-shadow neo-press flex items-center gap-2"
          >
            <Grid2x2 size={20} strokeWidth={3} />
            View Full Gallery ({SLIDES.length})
          </button>
        </div>
      </div>

      {/* GRID MODAL */}
      {gridOpen && (
        <div
          data-testid="gallery-grid-modal"
          className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setGridOpen(false); }}
        >
          <div className="sticky top-0 bg-slate-900 border-b-4 border-brand-yellow z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
              <h3 className="font-fredoka font-bold text-xl sm:text-2xl text-white">
                Full Gallery <span className="text-brand-yellow">— {SLIDES.length} photos</span>
              </h3>
              <button
                data-testid="gallery-grid-close"
                onClick={() => setGridOpen(false)}
                aria-label="Close gallery"
                className="w-11 h-11 grid place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm"
              >
                <X size={22} strokeWidth={3} />
              </button>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {SLIDES.map((s, i) => (
                <button
                  key={s.key}
                  data-testid={`gallery-grid-thumb-${i}`}
                  onClick={() => setLightboxIdx(i)}
                  className="group relative aspect-square overflow-hidden rounded-2xl border-4 border-slate-900 bg-white"
                >
                  <img
                    src={s.image}
                    alt={`Photo ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <span className="absolute bottom-2 right-2 bg-brand-yellow border-2 border-slate-900 rounded-full px-2 py-0.5 text-[10px] font-fredoka font-bold">
                    {i + 1}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightboxIdx !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[110] bg-black/95 grid place-items-center px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxIdx(null); }}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightboxIdx(null)}
            aria-label="Close"
            className="absolute top-4 right-4 w-12 h-12 grid place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm z-10"
          >
            <X size={24} strokeWidth={3} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={() => setLightboxIdx((i) => Math.max(0, (i ?? 0) - 1))}
            disabled={lightboxIdx === 0}
            aria-label="Previous"
            className="absolute left-2 sm:left-6 w-12 h-12 sm:w-14 sm:h-14 grid place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm disabled:opacity-30 z-10"
          >
            <ChevronLeft size={26} strokeWidth={3} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={() => setLightboxIdx((i) => Math.min(SLIDES.length - 1, (i ?? 0) + 1))}
            disabled={lightboxIdx === SLIDES.length - 1}
            aria-label="Next"
            className="absolute right-2 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 grid place-items-center rounded-full bg-white border-4 border-slate-900 neo-shadow-sm disabled:opacity-30 z-10"
          >
            <ChevronRight size={26} strokeWidth={3} />
          </button>
          <div className="relative max-w-6xl w-full max-h-[88vh]">
            <img
              src={SLIDES[lightboxIdx].image}
              alt={`Photo ${lightboxIdx + 1}`}
              className="w-full h-full max-h-[88vh] object-contain rounded-2xl"
            />
            <div className="absolute -top-3 left-3 bg-brand-yellow border-4 border-slate-900 rounded-full px-3 py-1 neo-shadow-sm font-fredoka font-bold text-sm">
              {lightboxIdx + 1} / {SLIDES.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
